import { useRef, useEffect } from "react";
import { useLoader } from "../../hooks/LoaderContext";

const navkeys = [
  "Backspace",
  "Delete",
  "Shift",
  "ArrowUp",
  "ArrowDown",
  "ArrowLeft",
  "ArrowRight",
  "Tab",
];

const getElementDatasetPropertyValue = (el, propertyName) => {
  if (el?.dataset && propertyName) {
    return el.dataset[propertyName];
  }
};

const deleteTargetDatasetProperty = (el, propertyName) => {
  if (el?.dataset && propertyName) {
    delete el.dataset[propertyName];
  }
};

const setCursorPositionInDataset = (el, position) => {
  if (el?.dataset) {
    el.dataset.cursorPosition = position;
  }
};

const setCaretPosition = (el, begin, end) => {
  if (!el) return;
  setCursorPositionInDataset(el, begin);
  el.setSelectionRange?.(begin, end);
};

const constructNewArray = (array) => {
  let newArray = array;
  const lastSlashIndex = array.lastIndexOf("/");
  const endArray = array.slice(lastSlashIndex + 1);
  const isLengthExceeded = endArray.length > 4;
  if (isLengthExceeded) {
    newArray = [...array.slice(0, lastSlashIndex + 1)].concat(endArray.slice(0, 4));
  }
  return newArray;
};

const dispatchInputEvent = (event) => {
  event.preventDefault();
  event.target.dispatchEvent(new Event("input", { bubbles: true }));
};

const incrementCaretPositionForSlash = (maskSlotChars, selectionStart) => {
  let newSelectionStart = selectionStart;
  if (maskSlotChars[selectionStart] === "/") {
    const firstLastAdjacentValue = maskSlotChars[selectionStart - 1];
    const secondLastAdjacentValue = maskSlotChars[selectionStart - 2];
    if (
      firstLastAdjacentValue &&
      secondLastAdjacentValue &&
      firstLastAdjacentValue !== "/" &&
      secondLastAdjacentValue !== "/"
    ) {
      newSelectionStart = selectionStart + 1;
    }
  }

  return newSelectionStart;
};

const setTargetNewValue = (
  event,
  selectionStart,
  pressedKey = "",
  maskSlotCharsRef,
  isValueCompletelySelected = false
) => {
  const target = event.target;
  if (isValueCompletelySelected) {
    maskSlotCharsRef.current = pressedKey ? [pressedKey, "/", "/"] : ["/", "/"];
    target.value = maskSlotCharsRef.current.join("");
    const selectedPosition = pressedKey ? 1 : 0;
    setCaretPosition(target, selectedPosition, selectedPosition);
    return dispatchInputEvent(event);
  }
  const deletedCount = pressedKey && maskSlotCharsRef.current[selectionStart] === "/" ? 0 : 1;
  const spliceArgs = pressedKey ? [selectionStart, deletedCount, pressedKey] : [selectionStart, 1];
  let selectionPostion = pressedKey ? selectionStart + 1 : selectionStart;
  maskSlotCharsRef.current.splice(...spliceArgs);
  maskSlotCharsRef.current = constructNewArray(maskSlotCharsRef.current);
  const newInputValue = [...maskSlotCharsRef.current].join("");
  target.value = newInputValue === "//" ? "" : newInputValue;
  selectionPostion = incrementCaretPositionForSlash(maskSlotCharsRef.current, selectionPostion);
  setCaretPosition(target, selectionPostion, selectionPostion);
  dispatchInputEvent(event);
};

const useCalendarDateEvents = (defaultValue = null, inputRef = null) => {
  const maskSlotCharsRef = useRef(["/", "/"]);
  const isDefaultValueSet = useRef(false);
  const { isLoaderEnabled } = useLoader();

  const onKeyDown = (e) => {
    const pressedKey = e.key;
    const target = e.target;
    const isValueCompletelySelected =
      target.selectionStart === 0 && target.selectionEnd === target.value.length;
    let selectionStart = e.target.selectionStart;
    if (e.code === "KeyA" && (e.ctrlKey || e.metaKey)) {
      e.target.select();
    }
    if (isNaN(pressedKey) && !navkeys.includes(pressedKey) && pressedKey !== "Enter")
      return e.preventDefault();
    if (!navkeys.includes(pressedKey) && pressedKey !== "Enter") {
      selectionStart = incrementCaretPositionForSlash(maskSlotCharsRef.current, selectionStart);
      setTargetNewValue(e, selectionStart, pressedKey, maskSlotCharsRef, isValueCompletelySelected);
    }

    if (["Backspace", "Delete"].includes(pressedKey)) {
      selectionStart = selectionStart - 1 <= 0 ? 0 : selectionStart - 1;
      if (maskSlotCharsRef.current[selectionStart] === "/" && !isValueCompletelySelected) {
        setCaretPosition(target, selectionStart, selectionStart);
        dispatchInputEvent(e);
      } else {
        setTargetNewValue(e, selectionStart, null, maskSlotCharsRef, isValueCompletelySelected);
      }
    }
  };

  const onBlur = (e) => {
    if (e.target.value === "//") {
      e.target.value = "";
    }

    deleteTargetDatasetProperty(e.target, "isTabKeyPressed");
  };

  const onFocus = (e) => {
    const target = e?.target;
    const isTabKeyPressed = getElementDatasetPropertyValue(target, "isTabKeyPressed") === "true";
    if (isTabKeyPressed) {
      deleteTargetDatasetProperty(target, "isTabKeyPressed");
    }
    const cursorPosition = getElementDatasetPropertyValue(target, "cursorPosition");
    if (cursorPosition) {
      setCaretPosition(target, cursorPosition, cursorPosition);
    }
    if (target.value && isTabKeyPressed) {
      target.select?.();
    }
    if (!target.value) {
      maskSlotCharsRef.current = ["/", "/"];
      target.value = "//";
      target.setSelectionRange(0, 0);
    }
  };

  const onHide = (target) => {
    if (target.value) {
      maskSlotCharsRef.current = target.value.split("");
    }
    if (!target.value) {
      maskSlotCharsRef.current = ["/", "/"];
      target.value = "//";
    }
  };

  useEffect(() => {
    if (defaultValue && !defaultValue.includes("invalid:") && !isDefaultValueSet.current) {
      maskSlotCharsRef.current = defaultValue.split("");
      isDefaultValueSet.current = true;
    }
  }, [defaultValue]);

  useEffect(() => {
    const inputEl = inputRef?.current || null;
    if (inputEl) {
      const mousedownEvent = (e) => {
        deleteTargetDatasetProperty(e.target, "isTabKeyPressed");
        setTimeout(() => {
          setCursorPositionInDataset(e.target, e.target.selectionStart);
        }, 10);
        if (!e.target.value) {
          e.target.value = "//";
          setTimeout(() => {
            e.target.setSelectionRange(0, 0);
          }, 1);
        }
      };
      const keydownListener = (e) => {
        if (e.key === "Tab") {
          inputRef.current.dataset.isTabKeyPressed = true;
        }
      };
      const backBtnTriggeredListener = () => {
        if (isDefaultValueSet.current) isDefaultValueSet.current = false;
        deleteTargetDatasetProperty(inputRef.current, "isTabKeyPressed");
      };
      window.addEventListener("keydown", keydownListener, true);
      window.addEventListener("BackButtonTriggered", backBtnTriggeredListener, true);
      inputEl.addEventListener("mousedown", mousedownEvent, true);

      return () => {
        inputEl.removeEventListener("mousedown", mousedownEvent, true);
        window.removeEventListener("keydown", keydownListener, true);
        window.removeEventListener("BackButtonTriggered", backBtnTriggeredListener, true);
      };
    }
  }, [inputRef]);

  useEffect(() => {
    if (inputRef.current && isLoaderEnabled) {
      deleteTargetDatasetProperty(inputRef.current, "isTabKeyPressed");
    }
  }, [isLoaderEnabled, inputRef]);

  return {
    onKeyDown,
    onBlur,
    onFocus,
    onHide,
    setCaretPosition,
  };
};

export default useCalendarDateEvents;